import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Link
} from '@mui/material';
import { NotificationsActive as NotifyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import { isPlatformLevel, can } from '../utils/roleUtils';

// Events that carry a "portal only" sub-option (must match the backend options bag).
const PORTAL_ONLY_EVENTS = new Set(['WORK_ORDER_CREATED']);

const NotificationSettings = ({ currentUser }) => {
  const navigate = useNavigate();
  const isPlatform = isPlatformLevel(currentUser?.role);
  const canEdit = can(currentUser, 'settings', 'write');

  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenter, setSelectedTypingCenter] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch typing centers (platform users pick one; TC users are scoped server-side).
  useEffect(() => {
    const fetchTypingCenters = async () => {
      try {
        const response = await ApiService.getTypingCenters();
        if (response.data?.data) {
          setTypingCenters(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedTypingCenter(currentUser?.typingCenterId || response.data.data[0].id);
          }
        }
      } catch (err) {
        console.error('Error fetching typing centers:', err);
        setError('Failed to load typing centers');
      }
    };
    fetchTypingCenters();
  }, [currentUser]);

  const fetchPreferences = useCallback(async () => {
    if (!selectedTypingCenter) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getNotificationPreferences(selectedTypingCenter);
      setEvents(response.data?.data?.events || []);
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [selectedTypingCenter]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Persist one event's desired {enabled, options}, then refresh from the server.
  const save = async (eventType, enabled, options) => {
    setSaving(eventType);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.updateNotificationPreference(
        { event_type: eventType, enabled, options }, selectedTypingCenter);
      setSuccess('Notification settings saved');
      await fetchPreferences();
    } catch (err) {
      console.error('Error saving notification preference:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save notification settings');
      await fetchPreferences();
    } finally {
      setSaving(null);
    }
  };

  const handleToggleEnabled = (ev) => (e) => {
    save(ev.event_type, e.target.checked, ev.options || {});
  };

  const handleTogglePortalOnly = (ev) => (e) => {
    save(ev.event_type, ev.enabled, { ...(ev.options || {}), portal_only: e.target.checked });
  };

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Box display="flex" alignItems="center" mb={0.5}>
        <NotifyIcon sx={{ mr: 1, color: 'secondary.main' }} />
        <Typography variant="h5" fontWeight={600}>
          Notification Settings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 620 }}>
        Choose which events email your typing center. Messages are sent to your{' '}
        <Link component="button" type="button" underline="hover" onClick={() => navigate('/settings?section=company&tab=details')}>
          Company Settings
        </Link>{' '}
        email and need SMTP set up in{' '}
        <Link component="button" type="button" underline="hover" onClick={() => navigate('/settings?section=communication&tab=email')}>
          Email Settings
        </Link>.
      </Typography>

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}

      {isPlatform && typingCenters.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Typing center</InputLabel>
            <Select
              value={selectedTypingCenter}
              label="Typing center"
              onChange={(e) => setSelectedTypingCenter(e.target.value)}
            >
              {typingCenters.map((tc) => (
                <MenuItem key={tc.id} value={tc.id}>{tc.name} ({tc.code})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : events.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            There are no configurable notifications yet.
          </Typography>
        </Paper>
      ) : (
        events.map((ev) => {
          const options = ev.options || {};
          const isSaving = saving === ev.event_type;
          const hasPortalOption = PORTAL_ONLY_EVENTS.has(ev.event_type);
          return (
            <Paper key={ev.event_type} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {ev.display_name || ev.event_type}
                  </Typography>
                  {ev.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 460 }}>
                      {ev.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {isSaving && <CircularProgress size={18} />}
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={!!ev.enabled}
                        onChange={handleToggleEnabled(ev)}
                        disabled={isSaving || !canEdit}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" color={ev.enabled ? 'text.primary' : 'text.secondary'}>
                        {ev.enabled ? 'On' : 'Off'}
                      </Typography>
                    }
                  />
                </Box>
              </Box>

              {hasPortalOption && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    opacity: ev.enabled ? 1 : 0.55
                  }}
                >
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={!!options.portal_only}
                        onChange={handleTogglePortalOnly(ev)}
                        disabled={isSaving || !ev.enabled || !canEdit}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Only for work orders submitted via the customer portal
                      </Typography>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
                    Leave off to also email work orders your staff create directly.
                  </Typography>
                </Box>
              )}
            </Paper>
          );
        })
      )}
    </Box>
  );
};

export default NotificationSettings;
